import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { RootState, ThunkServices } from '../../app/store';

export interface CountiesState {
    countiesAvailable: endpoints.County[];
    loading: boolean;
}

export const initialState: CountiesState = {
    countiesAvailable: [],
    loading: false,
};

// Thunks
export const getCounties = createAsyncThunk<
    endpoints.County[], // Return type of the thunk
    void, // Argument type (none in this case)
    { state: RootState; extra: ThunkServices } // ThunkAPI configuration
>('counties/getCounties', async (_, { extra: { countyService } }) => {
    const counties = import.meta.env.VITE_DS_COUNTIES;
    if (!counties) {
        throw new Error('No counties set');
    }
    const response = await countyService.getCounties();
    return response;
});

// Slice
export const countiesSlice = createSlice({
    name: 'counties',
    initialState,
    reducers: {
        setCounties: (state, action: PayloadAction<{ countiesAvailable: endpoints.County[] }>) => {
            if (action.payload !== undefined && action.payload.countiesAvailable !== undefined) {
                state.countiesAvailable = action.payload.countiesAvailable; // Update countiesAvailable
            }
        },
        setCountiesLoading: (state, action: PayloadAction<{ loading: boolean }>) => {
            if (action.payload.loading !== undefined) {
                state.loading = action.payload.loading; // Update loading state
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getCounties.pending, (state) => {
                state.loading = true; // Automatically set loading when the thunk is pending
            })
            .addCase(getCounties.fulfilled, (state, action) => {
                state.countiesAvailable = action.payload; // Update countiesAvailable when the thunk is fulfilled
                state.loading = false; // Set loading to false
            })
            .addCase(getCounties.rejected, (state) => {
                state.loading = false; // Set loading to false when the thunk is rejected
            });
    },
});

// Actions
export const { setCounties, setCountiesLoading } = countiesSlice.actions;

// Reducer
export const countiesReducer = countiesSlice.reducer;